import React from "react";
/**
 * Hook for managing box movements between pallets
 * Handles target selection, movement tracking, new pallet creation, and modal state
 */
export const useBoxMovement = () => {
	const [isBoxMovementMode, setIsBoxMovementMode] = React.useState(false);
	const [isConfirmBoxMovementMode, setIsConfirmBoxMovementMode] = React.useState(false);
	const [isSelectingTarget, setIsSelectingTarget] = React.useState(false);
	const [targetPallet, setTargetPallet] = React.useState(null);
	// Keyed by pallet id: { [palletId]: { count_boxes, boxes? } } — boxes is present only for manual picking.
	const [boxMovements, setBoxMovements] = React.useState({});
	const [isCreatingNewPallet, setIsCreatingNewPallet] = React.useState(false);

	const [movementModalOpen, setMovementModalOpen] = React.useState(false);
	const [selectedSourcePallet, setSelectedSourcePallet] = React.useState(null);

	// Keep a ref to the latest movements so isBoxPicked stays referentially stable
	const boxMovementsRef = React.useRef(boxMovements);
	React.useEffect(() => {
		boxMovementsRef.current = boxMovements;
	}, [boxMovements]);

	// Box-movement functions
	const startBoxMovement = () => {
		setIsSelectingTarget(true);
		setIsCreatingNewPallet(false);
	};

	const selectTargetPallet = (pallet) => {
		setTargetPallet(pallet);
		setIsSelectingTarget(false);
		setIsBoxMovementMode(true);
		setBoxMovements({});
	};

	const cancelBoxMovement = () => {
		setIsBoxMovementMode(false);
		setIsSelectingTarget(false);
		setTargetPallet(null);
		setBoxMovements({});
		setIsCreatingNewPallet(false);
	};

	// Replace a pallet's picked boxes; dropping to zero removes the entry entirely.
	const setPalletBoxes = (palletId, updateBoxes) => {
		setBoxMovements((prev) => {
			const boxes = updateBoxes(prev[palletId]?.boxes || []);
			if (boxes.length === 0) {
				const { [palletId]: _removed, ...rest } = prev;
				return rest;
			}
			return { ...prev, [palletId]: { boxes, count_boxes: boxes.length } };
		});
	};

	const addBoxMovement = ({ pallet_id, count_boxes }) => {
		setBoxMovements((prev) => ({
			...prev,
			[pallet_id]: { count_boxes: (prev[pallet_id]?.count_boxes || 0) + count_boxes },
		}));
	};

	const removeBoxMovement = (palletId) => {
		setBoxMovements((prev) => {
			const { [palletId]: _removed, ...rest } = prev;
			return rest;
		});
	};

	const isBoxPicked = React.useCallback(
		(palletId, boxId) =>
			boxMovementsRef.current[palletId]?.boxes?.some((b) => b.box_id === boxId) ?? false,
		[]
	);

	const pickBox = (pallet, box, byScanner = false) => {
		setPalletBoxes(pallet.id, (boxes) =>
			boxes.some((b) => b.box_id === box.id)
				? boxes
				: [...boxes, { box_id: box.id, by_scanner: byScanner }]
		);
	};

	const unpickBox = (palletId, boxId) => {
		setPalletBoxes(palletId, (boxes) => boxes.filter((b) => b.box_id !== boxId));
	};

	const getPickedBoxIds = (palletId) => (boxMovements[palletId]?.boxes || []).map((b) => b.box_id);

	const createNewPallet = () => {
		setIsCreatingNewPallet(true);
		setTargetPallet({ id: null, sscc: "New Pallet" });
		setIsSelectingTarget(false);
		setIsBoxMovementMode(true);
	};

	const cancelNewPallet = () => {
		setIsCreatingNewPallet(false);
		setTargetPallet(null);
		setIsSelectingTarget(true);
		setIsBoxMovementMode(false);
	};

	const handleOpenMovementModal = (pallet) => {
		setSelectedSourcePallet(pallet);
		setMovementModalOpen(true);
	};

	const handleCloseMovementModal = () => {
		setMovementModalOpen(false);
		setSelectedSourcePallet(null);
	};

	const handleConfirmMovement = (movement) => {
		addBoxMovement(movement);
	};

	return {
		isBoxMovementMode,
		isConfirmBoxMovementMode,
		isSelectingTarget,
		targetPallet,
		boxMovements,
		isCreatingNewPallet,
		movementModalOpen,
		selectedSourcePallet,
		setIsConfirmBoxMovementMode,
		startBoxMovement,
		selectTargetPallet,
		cancelBoxMovement,
		addBoxMovement,
		removeBoxMovement,
		isBoxPicked,
		pickBox,
		unpickBox,
		getPickedBoxIds,
		createNewPallet,
		cancelNewPallet,
		handleOpenMovementModal,
		handleCloseMovementModal,
		handleConfirmMovement,
	};
};
